import type {
  DocumentUploadTypesConfig,
  LaserficheConfiguration,
  LaserficheInspectField,
  LaserficheInspectTag,
  LaserficheInspectTemplate,
  LaserficheMetadata,
  LaserficheMetadataConfigKey,
  LaserfichePushType,
  UploadTypeId,
} from "@repo/types";
import { useMemo, useState } from "react";
import { LaserficheMetadataPush } from "./LaserficheMetadataPush";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { Button } from "@repo/ui/components/Base/Button";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { PlusIcon } from "lucide-react";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import type { UpdateLaserficheMetadata } from "@/repositories/laserficheIntegrationRepo";
import { tenantStore } from "@/stores/tenantStore";
import { useGetDocumentUploadTypes } from "@/hooks/queryHooks/configHooks";
import { useLaserficheGetMetadata } from "@/hooks/queryHooks/laserficheIntegrationHooks";

// #region Utilities

const pushTypeDetails: Record<
  LaserfichePushType,
  { label: string; description: string; show?: () => boolean }
> = {
  agendas: {
    label: "Meeting Agendas",
    description:
      "When a meeting is published, it will be copied to Laserfiche.",
  },
  documents: {
    label: "Attached Documents",
    description:
      "When a meeting is published, all attached documents will be copied to Laserfiche.",
  },
  minutes: {
    label: "Meeting Minutes",
    description:
      "When a meeting's minutes are marked official, the minutes will be copied to Laserfiche.",
  },
  signed: {
    label: "Signed Meeting Documents",
    show: () => !!tenantStore.getState().tenant?.featureFlags?.signatures,
    description:
      "When a document on a run meeting is signed, the signed version will be copied to Laserfiche.",
  },
  orphanSigned: {
    label: "Signed Orphan Documents",
    show: () => !!tenantStore.getState().tenant?.featureFlags?.signatures,
    description:
      "When a document that is not in a run meeting is signed, it will be copied to Laserfiche.",
  },
};

interface AvailableMetadata {
  fields: LaserficheInspectField[];
  templates: LaserficheInspectTemplate[];
  tags: LaserficheInspectTag[];
}

// #endregion

// #region Basic Push

interface PushTypeConfigProps {
  disabled?: boolean;
  pushType: LaserfichePushType;
  available?: AvailableMetadata;
  initialMetadata?: LaserficheMetadata;
  onSave?: LaserficheMetadataSaveHandler;
}

const PushTypeConfig = ({
  disabled,
  pushType,
  available,
  initialMetadata,
  onSave,
}: PushTypeConfigProps) => {
  const details = pushTypeDetails[pushType];

  return (
    <div
      key={pushType}
      className="flex flex-col gap-3 bg-gray-100 rounded-lg p-5"
    >
      <p className="text-lg">{details.label}</p>

      {available ? (
        <LaserficheMetadataPush
          disabled={disabled}
          availableMetadata={available}
          pushType={pushType}
          initialMetadata={initialMetadata}
          enableFields={details.description}
          onSave={(meta) =>
            onSave?.({
              key: { type: "basic", subType: pushType },
              config: meta,
            })
          }
        />
      ) : (
        <Spinner />
      )}
    </div>
  );
};

// #endregion

// #region DocType Push

interface DocumentTypePushConfigProps {
  disabled?: boolean;
  documentType: UploadTypeId;
  uploadTypeMap: Record<
    UploadTypeId,
    DocumentUploadTypesConfig["uploadTypes"][0]
  >;
  available?: AvailableMetadata;
  initialMetadata?: LaserficheMetadata;
  onSave?: LaserficheMetadataSaveHandler;
  onDelete?: LaserficheMetadataDeleteHandler;
}

const DocumentTypePushConfig = ({
  disabled,
  documentType,
  uploadTypeMap,
  available,
  initialMetadata,
  onSave,
  onDelete,
}: DocumentTypePushConfigProps) => {
  return (
    <div className="flex flex-col bg-gray-100 rounded-lg p-5">
      <p className="text-lg mb-3">
        Metadata for: {uploadTypeMap[documentType].label}
      </p>

      {available ? (
        <LaserficheMetadataPush
          disabled={disabled}
          availableMetadata={available}
          pushType="documents"
          initialMetadata={initialMetadata}
          onSave={(meta) =>
            onSave?.({
              key: { type: "documentType", subType: documentType },
              config: meta,
            })
          }
          onDelete={() => {
            onDelete?.({ type: "documentType", subType: documentType });
          }}
        />
      ) : (
        <Spinner />
      )}
    </div>
  );
};

// #endregion

// #region Main Export

export type LaserficheMetadataSaveHandler = (
  meta: UpdateLaserficheMetadata
) => void | Promise<void>;

export type LaserficheMetadataDeleteHandler = (
  meta: LaserficheMetadataConfigKey
) => void | Promise<void>;

export interface LaserficheMetadataProps {
  disabled?: boolean;
  tenantId: string;
  teamId?: string | null;
  signatures?: boolean;
  label?: React.ReactNode;
  initialMetadata: LaserficheConfiguration["metadata"] | null;
  onSave?: LaserficheMetadataSaveHandler;
  onDelete?: LaserficheMetadataDeleteHandler;
}

export const LaserficheMetadataConfig = ({
  disabled = false,
  tenantId,
  teamId = null,
  signatures = false,
  initialMetadata,
  onSave,
  onDelete,
}: LaserficheMetadataProps) => {
  const { data: available } = useLaserficheGetMetadata(tenantId, teamId);
  const { data: uploadTypes } = useGetDocumentUploadTypes();

  const uploadTypeMap = useMemo(
    () =>
      uploadTypes?.uploadTypes.reduce(
        (map, uploadType) => ((map[uploadType.id] = uploadType), map),
        Object.create(null) as Record<
          string,
          DocumentUploadTypesConfig["uploadTypes"][0]
        >
      ),
    [uploadTypes]
  );
  const documentTypes = useMemo(
    () => uploadTypes?.uploadTypes.map((type) => type.id),
    [uploadTypes]
  );

  onSave = useFutureCallback(onSave ?? (() => {}));

  const baseMetaConfigs = useMemo(
    () =>
      (Object.keys(pushTypeDetails) as LaserfichePushType[]).map((pushType) => {
        const details = pushTypeDetails[pushType];
        if (details.show && !details.show()) return null;

        return (
          <PushTypeConfig
            key={pushType}
            disabled={disabled}
            pushType={pushType}
            available={available}
            initialMetadata={initialMetadata?.[pushType] ?? undefined}
            onSave={onSave}
          />
        );
      }),
    [available, disabled, initialMetadata, onSave]
  );

  const [newDocumentType, setNewDocumentType] = useState<UploadTypeId | "">("");
  const [documentTypeMetas, setDocumentTypeMetas] = useState<UploadTypeId[]>(
    initialMetadata?.documentType
      ? (Object.keys(initialMetadata?.documentType) as UploadTypeId[])
      : []
  );

  const availableDocumentTypes = useMemo(() => {
    if (!documentTypes) return [];
    const documentTypeSet = new Set<UploadTypeId>(documentTypes);
    for (const type of documentTypeMetas) documentTypeSet.delete(type);
    return [...documentTypeSet];
  }, [documentTypes, documentTypeMetas]);

  if (!documentTypes || !uploadTypeMap) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-lg font-bold mb-2">Metadata Configuration</p>
        <p>
          Configure Laserfiche metadata to assign to meeting agendas, attached
          documents,
          {signatures
            ? " official minutes, and orphaned signed documents "
            : " and official minutes "}
          when sent to Laserfiche.
        </p>
      </div>

      {baseMetaConfigs}

      <div>
        <p className="text-lg font-bold mb-2">
          Configure Metadata by Document Type
        </p>
        <p>
          Pick a type of document below, and click "Add Metadata for Type" to
          configure metadata to assign to documents sent to Laserfiche based on
          the document type.
        </p>
      </div>
      {documentTypeMetas.map((type) => (
        <DocumentTypePushConfig
          key={type}
          disabled={disabled}
          documentType={type}
          uploadTypeMap={uploadTypeMap}
          available={available}
          initialMetadata={initialMetadata?.documentType?.[type]}
          onSave={onSave}
          onDelete={(key) => {
            // sanity check
            if (key.type === "documentType") {
              const newDocumentTypeMetas = documentTypeMetas.filter(
                (documentTypeMeta) => documentTypeMeta !== key.subType
              );
              setDocumentTypeMetas(newDocumentTypeMetas);
            }
            onDelete?.(key);
          }}
        />
      ))}

      <div className="flex flex-row items-end gap-4">
        <SelectInput
          label="Document Type"
          value={newDocumentType}
          onValueChange={(value) => setNewDocumentType(value as UploadTypeId)}
          options={availableDocumentTypes.map((type) => (
            <SelectItem key={type} value={type}>
              {uploadTypeMap[type].label}
            </SelectItem>
          ))}
          containerStyle="w-full max-w-80"
        />
        <Button
          onClick={() => {
            if (!newDocumentType) return;

            const newDocumentTypeMetas = [
              ...documentTypeMetas,
              newDocumentType,
            ];
            setDocumentTypeMetas(newDocumentTypeMetas);
            setNewDocumentType("");
          }}
        >
          <PlusIcon /> Add Metadata for Type
        </Button>
      </div>
    </div>
  );
};

// #endregion
